import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  HallOption,
  canDownloadOrCancel,
  cancelHallBooking,
  downloadApprovalLetter,
  fetchMasterOptions,
  fieldOf,
  findOneHallBooking,
  getRequestStatusUi,
  resolveOptionLabel,
  saveBase64File,
} from "./communityHall.service";

const PAGE_TITLE = "View Booking of Community Hall/Common Room/Park";

const ViewCommunityHallBooking: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const routes = all_routes;
  const { referenceNumber } = useParams<{ referenceNumber: string }>();
  const passed = (location.state as { request?: any } | null)?.request;
  const referenceId = decodeURIComponent(referenceNumber || "") || fieldOf(passed, "ReferenceNumber", "referenceNumber", "id");
  const label = (key: string, fallback: string) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const [details, setDetails] = useState<any>(passed ?? null);
  const [loading, setLoading] = useState(false);
  const [requestTypes, setRequestTypes] = useState<HallOption[]>([]);
  const [staffQuarters, setStaffQuarters] = useState<HallOption[]>([]);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const field = (...keys: string[]) => fieldOf(details, ...keys);

  useEffect(() => {
    fetchMasterOptions()
      .then((options) => {
        setRequestTypes(options.requestTypes);
        setStaffQuarters(options.staffQuarters);
      })
      .catch(() => {});
  }, []);

  const loadDetails = async () => {
    if (!referenceId) return;
    setLoading(true);
    try {
      const data = await findOneHallBooking(referenceId);
      if (data) setDetails(data);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceId]);

  const requestTypeCode = field("RequestType", "requestType");
  const staffQuarterCode = field("StaffQuarter", "staffQuarter");
  const status = field("RequestStatus");
  const statusUi = getRequestStatusUi(status);
  const showActions = canDownloadOrCancel(status);

  const handleDownload = async () => {
    const ref = field("ReferenceNumber", "referenceNumber", "id") || referenceId;
    setLoading(true);
    try {
      const file = await downloadApprovalLetter(ref);
      saveBase64File(file.content, file.fileName);
    } catch (err: any) {
      toast.error(err?.message || Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const submitCancellation = async () => {
    const empId = field("EmpID", "empId");
    const aReferenceNumber = field("ReferenceNumber", "referenceNumber", "id") || referenceId;
    setConfirmCancel(false);
    setLoading(true);
    try {
      const result = await cancelHallBooking(empId, aReferenceNumber);
      if (result.ok) {
        toast.success(result.message || label("request_cancelled_successfully", "Request cancelled successfully."));
        await loadDetails();
      } else {
        toast.error(result.message || label("something_went_wrong", "Something went wrong"));
      }
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{label("view_booking_of_community_hall_common_room_park", PAGE_TITLE)}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.adminDashboard}>Dashboard</Link></li>
                <li className="breadcrumb-item">
                  <Link to={routes.communityHall.list}>{label("booking_of_community_hall_common_room_park", "Booking of Community Hall/Common Room/Park")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">{label("view_details", "View Details")}</li>
              </ol>
            </nav>
          </div>
          <button type="button" className="btn btn-light" onClick={() => navigate(routes.communityHall.list)}>
            <i className="ti ti-arrow-left me-1"></i>
            {label("back", "Back")}
          </button>
        </div>

        {loading && !details ? (
          <div className="text-center py-5"><div className="spinner-border text-danger" role="status" /></div>
        ) : !details ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">{label("no_data_found", "No data found")}</div>
          </div>
        ) : (
          <>
            {showActions && (
              <div className="d-flex flex-wrap gap-2 mb-3">
                <button type="button" className="btn btn-danger" disabled={loading} onClick={handleDownload}>
                  <i className="ti ti-download me-1"></i>
                  {label("download_approval_letter", "Download Approval Letter")}
                </button>
                <button type="button" className="btn btn-outline-danger" disabled={loading} onClick={() => setConfirmCancel(true)}>
                  <i className="ti ti-x me-1"></i>
                  {label("application_cancellation", "Application Cancellation")}
                </button>
              </div>
            )}

            <div className="alert alert-light d-flex justify-content-between align-items-center">
              <span className="fw-semibold">{label("request_status", "Request Status")}</span>
              <span className={statusUi.badge}>{statusUi.label}</span>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white fw-bold">{label("application_info", "Application Info")}</div>
              <div className="card-body">
                <div className="row">
                  <Info label={label("reference_no", "Reference No")} value={field("ReferenceNumber", "referenceNumber", "id") || referenceId} />
                  <Info label={label("personnel_no", "Personnel No.")} value={field("EmpID", "empId")} />
                  <Info
                    label={label("request_type", "Request Type")}
                    value={field("AReferenceNumber") ? label("application_cancellation", "Application Cancellation") : label("booking_request", "Booking request")}
                  />
                  <Info label={label("request_type_small", "request type")} value={resolveOptionLabel(requestTypes, requestTypeCode) || requestTypeCode} />
                  <Info label={label("staff_quarter", "Staff Quarters")} value={resolveOptionLabel(staffQuarters, staffQuarterCode) || staffQuarterCode} />
                  <Info label={t("start_date")} value={field("BookingBeginDateDisplay", "bookingBeginDate", "BookingBeginDate")} />
                  <Info label={t("end_date")} value={field("BookingEndDateDisplay", "bookingEndDate", "BookingEndDate")} />
                  <Info label={label("amount", "Amount")} value={field("BookingAmount", "TotalAmount", "amount", "Amount")} />
                  <Info label={label("request_creation_date", "Request Creation Date")} value={field("SubmissionDateDisplay", "RequestCreationDate", "requestCreationDate")} />
                  <Info label={label("approver_user_id", "Approver User ID")} value={field("ApproverUserID", "approverUserID")} />
                  <Info label={label("approver_level", "Approver Level")} value={field("ApproverLevel", "approverLevel")} />
                  <Info label={label("approver_remarks", "Approver Remarks")} value={field("HRemarks", "approverRemarks", "ApproverRemarks")} />
                  <Info label={label("approver_name", "Approver Name")} value={field("ApproverName", "approverName")} />
                  <Info label={label("associated_refrence_no", "Associated Reference No")} value={field("AReferenceNumber")} />
                  <Info label={label("contact_number", "Contact Number")} value={field("ContactNumber", "contactNumber")} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {confirmCancel && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,0.45)" }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                {label("are_you_sure_you_want_to_cancel_this_booking", "Are you sure you want to cancel this booking?")}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setConfirmCancel(false)}>{label("no", "No")}</button>
                <button type="button" className="btn btn-danger" onClick={submitCancellation}>{label("yes", "Yes")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Info: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="col-md-6 mb-3">
    <div className="text-muted small fw-semibold">{label}</div>
    <div className="fw-medium">{value || "-"}</div>
  </div>
);

export default ViewCommunityHallBooking;
